import React, { useState, useMemo } from 'react';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { Book as BookIcon, BookOpen, Calendar, ArrowUpRight, Search, X, ChevronLeft, ChevronRight, Clock, User, Sparkles, Star, Copy, Check } from 'lucide-react';
import { books, BOOK_CATEGORIES, ARCHIVE_BOOK_CATEGORIES, Book } from '@/data/books';

const Books: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedArchiveCategory, setSelectedArchiveCategory] = useState<string | null>(null);
  const [copiedBookId, setCopiedBookId] = useState<string | null>(null);
  const itemsPerPage = 10;

  const filteredBooks = useMemo(() => {
    return books.filter(book => {
      const matchesSearch = searchQuery === '' || 
        book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (book.excerpt && book.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
        book.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesArchiveStatus = showArchived ? book.archived : !book.archived;
      
      let matchesCategory;
      if (showArchived) {
        matchesCategory = !selectedArchiveCategory || book.archiveCategory === selectedArchiveCategory;
      } else {
        matchesCategory = !selectedCategory || book.category === selectedCategory;
      }

      return matchesSearch && matchesCategory && matchesArchiveStatus;
    });
  }, [searchQuery, selectedCategory, selectedArchiveCategory, showArchived]);

  // Pagination logic
  const totalPages = Math.ceil(filteredBooks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBooks = filteredBooks.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedArchiveCategory, showArchived]);

  // Copy book title to clipboard
  const copyBookTitle = async (bookTitle: string, bookId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(bookTitle);
      setCopiedBookId(bookId);
      setTimeout(() => {
        setCopiedBookId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get status color and icon
  const getStatusInfo = (status: Book['status']) => {
    switch (status) {
      case 'Currently Reading':
        return {
          color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
          icon: '📖'
        };
      case 'Completed':
        return {
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          icon: '✅'
        };
      case 'Want to Read':
        return {
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
          icon: '📚'
        };
      case 'On Hold':
        return {
          color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
          icon: '⏸️'
        };
      default:
        return {
          color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
          icon: '📋'
        };
    }
  };

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      
      <main className="pt-14 pb-10 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="relative mb-6">
            <div className="relative bg-white/70 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-gray-200/60 dark:border-gray-700/50">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="p-1.5 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Bookshelf</h1>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-xl mx-auto text-center">
                {showArchived 
                  ? "Books I've completed and archived" 
                  : "Books I'm reading or planning to read on technology, engineering, personal development, and life"
                }
              </p>
            </div>
          </div>

          {/* Search and Category Filter */}
          <div className="space-y-4 mb-6">
            {/* Archive Toggle */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => {
                  setShowArchived(false);
                  setSelectedCategory(null);
                  setSelectedArchiveCategory(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !showArchived
                    ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                    : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80'
                }`}
              >
                Current Books
              </button>
              <button
                onClick={() => {
                  setShowArchived(true);
                  setSelectedCategory(null);
                  setSelectedArchiveCategory(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showArchived
                    ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                    : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80'
                }`}
              >
                Archive
              </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={showArchived ? "Search archived books..." : "Search books by title, author, or tags..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-800/60 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Category Filter - Compact pill bar */}
            <div className="relative">
              <div className="-mx-4 md:mx-0 overflow-x-auto no-scrollbar">
                <div className="px-4 md:px-0 min-w-full">
                  <div className="flex items-center gap-2 md:gap-2.5 flex-nowrap">
                    {!showArchived ? (
                      <>
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs transition-colors border ${
                            !selectedCategory
                              ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white border-transparent'
                              : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80 border-gray-200/60 dark:border-gray-700/50'
                          }`}
                        >
                          All Categories
                        </button>
                        {BOOK_CATEGORIES.map(category => (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs transition-colors border ${
                              selectedCategory === category
                                ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white border-transparent'
                                : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80 border-gray-200/60 dark:border-gray-700/50'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setSelectedArchiveCategory(null)}
                          className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs transition-colors border ${
                            !selectedArchiveCategory
                              ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white border-transparent'
                              : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80 border-gray-200/60 dark:border-gray-700/50'
                          }`}
                        >
                          All Archived
                        </button>
                        {ARCHIVE_BOOK_CATEGORIES.slice(1).map(category => (
                          <button
                            key={category}
                            onClick={() => setSelectedArchiveCategory(category)}
                            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs transition-colors border ${
                              selectedArchiveCategory === category
                                ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white border-transparent'
                                : 'bg-white/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/80 border-gray-200/60 dark:border-gray-700/50'
                            }`}
                          >
                            {category}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Books List */}
          <div className="space-y-2">
            {currentBooks.map((book) => {
              const statusInfo = getStatusInfo(book.status);
              return (
                <a
                  key={book.id}
                  href={book.link}
                  target={book.link ? "_blank" : undefined}
                  rel={book.link ? "noopener noreferrer" : undefined}
                  className={`group flex items-start gap-3 rounded-xl border border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-800/60 backdrop-blur-sm p-3 hover:shadow-md transition-all ${!book.link ? 'cursor-default' : ''}`}
                >
                  {/* Thumbnail */}
                  <div className="shrink-0 w-16 h-16 rounded-md bg-gradient-to-br from-green-500/20 to-blue-500/20 border border-gray-200/60 dark:border-gray-700/50 flex items-center justify-center">
                    <BookIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    {/* Title with Copy Button */}
                    <div className="flex items-start gap-2">
                      <h2 className="text-sm md:text-base font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400 line-clamp-2 flex-1">
                        {book.title}
                      </h2>
                      <button
                        onClick={(e) => copyBookTitle(book.title, book.id, e)}
                        className="shrink-0 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Copy book title"
                      >
                        {copiedBookId === book.id ? (
                          <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        )}
                      </button>
                    </div>
                    
                    {/* Excerpt */}
                    {book.excerpt && (
                      <p className="mt-1 text-[12px] md:text-[13px] text-gray-600 dark:text-gray-400 line-clamp-2">
                        {book.excerpt}
                      </p>
                    )}

                    {/* Meta */}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1 font-medium">
                        <User className="w-3 h-3" />
                        {book.author}
                      </span>
                      {book.startedAt && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Started {book.startedAt}
                        </span>
                      )}
                      {book.completedAt && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Completed {book.completedAt}
                        </span>
                      )}
                      {book.readingTime && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {book.readingTime}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {book.category}
                      </span>
                      {/* Status */}
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        <span>{statusInfo.icon}</span>
                        {book.status}
                      </span>
                      {/* Rating */}
                      {book.rating && (
                        <span className="inline-flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {book.rating}/5
                        </span>
                      )}
                      {/* Tags (first 2) */}
                      <span className="inline-flex items-center gap-1">
                        {book.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {book.tags.length > 2 && (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full">
                            +{book.tags.length - 2}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Chevron */}
                  {book.link && (
                    <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 mt-1" />
                  )}
                </a>
              );
            })}
          </div>

          {/* No Results */}
          {filteredBooks.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-block p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                <BookOpen className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                No books found matching your search criteria.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory(null);
                  setSelectedArchiveCategory(null);
                }}
                className="mt-4 text-green-600 dark:text-green-400 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between">
              {/* Results Info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredBooks.length)} of {filteredBooks.length} books
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    const shouldShow = 
                      page === 1 || 
                      page === totalPages || 
                      Math.abs(page - currentPage) <= 1;

                    if (shouldShow) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="px-2 text-gray-400">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <style>
        {`
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </div>
  );
};

export default Books;

